<template>
    <div class="promotions" v-if="$can('student_promote')">
        <v-snackbar v-model="snackbar" :timeout="4000" bottom right :color="color">
            <span>{{ message }}</span>
        </v-snackbar>

        <!-- Confirmation Dialog -->
        <v-dialog v-model="confirmDialog" max-width="500">
            <v-card>
                <v-card-title class="text-h5">
                    Confirm Promotion
                </v-card-title>
                <v-card-text>
                    <div v-if="promotionPreview">
                        <p>You are about to promote <strong>{{ promotionPreview.total }}</strong> students:</p>
                        <ul>
                            <li><strong>{{ promotionPreview.to_promote }}</strong> will be promoted to the next grade</li>
                            <li><strong>{{ promotionPreview.to_graduate }}</strong> will be graduated (alumni)</li>
                        </ul>
                        <p class="mt-3 warning--text">You can revert promotions from the Promotion History tab if needed.</p>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="confirmDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="promotionLoading" @click="executePromotion">
                        Confirm Promotion
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Revert Confirmation Dialog -->
        <v-dialog v-model="revertDialog" max-width="500">
            <v-card>
                <v-card-title class="text-h5">
                    <v-icon left color="warning">mdi-undo</v-icon>
                    Confirm Revert Promotion
                </v-card-title>
                <v-card-text v-if="promotionToRevert">
                    <p>You are about to revert this promotion:</p>
                    <v-simple-table dense class="my-3">
                        <tbody>
                            <tr>
                                <td class="font-weight-bold">Student:</td>
                                <td>{{ promotionToRevert.student?.full_name }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">From:</td>
                                <td>{{ promotionToRevert.from_sub_level?.name || 'N/A' }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">To:</td>
                                <td>{{ promotionToRevert.to_sub_level?.name || 'Graduated' }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Type:</td>
                                <td>{{ promotionToRevert.type }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-alert type="warning" dense class="mt-3">
                        <span v-if="promotionToRevert.type === 'graduation'">
                            This will move the student back from Alumni status to {{ promotionToRevert.from_sub_level?.name || 'their previous class' }}.
                        </span>
                        <span v-else>
                            This will move the student back to {{ promotionToRevert.from_sub_level?.name || 'their previous class' }}.
                        </span>
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="revertDialog = false">Cancel</v-btn>
                    <v-btn color="warning" :loading="revertLoading" @click="executeRevert">
                        <v-icon left>mdi-undo</v-icon>
                        Revert Promotion
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Bulk Revert Confirmation Dialog -->
        <v-dialog v-model="bulkRevertDialog" max-width="600">
            <v-card>
                <v-card-title class="text-h5">
                    <v-icon left color="warning">mdi-undo-variant</v-icon>
                    Confirm Bulk Revert
                </v-card-title>
                <v-card-text>
                    <p>You are about to revert <strong>{{ selectedPromotions.length }}</strong> promotions:</p>

                    <v-simple-table dense class="my-3" style="max-height: 300px; overflow-y: auto;">
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="promotion in selectedPromotions" :key="promotion.id">
                                <td>{{ promotion.student?.full_name }}</td>
                                <td>{{ promotion.from_sub_level?.name || 'N/A' }}</td>
                                <td>{{ promotion.to_sub_level?.name || 'Graduated' }}</td>
                                <td>
                                    <v-chip x-small :color="getPromotionTypeColor(promotion.type)">
                                        {{ promotion.type }}
                                    </v-chip>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <v-alert type="warning" dense class="mt-3">
                        All selected students will be moved back to their previous class/grade.
                        Graduated students will be removed from Alumni status.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="bulkRevertDialog = false">Cancel</v-btn>
                    <v-btn color="warning" :loading="bulkRevertLoading" @click="executeBulkRevert">
                        <v-icon left>mdi-undo-variant</v-icon>
                        Revert {{ selectedPromotions.length }} Promotion(s)
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div class="pa-5">
            <v-layout column>
                <!-- Main Tabs -->
                <v-tabs v-model="mainTab" class="mb-5">
                    <v-tab>Class Promotion</v-tab>
                    <v-tab>Manual Promotion</v-tab>
                    <v-tab>Promotion History</v-tab>
                    <v-tab>Alumni</v-tab>
                </v-tabs>

                <v-tabs-items v-model="mainTab">
                    <!-- Class Promotion Tab -->
                    <v-tab-item>
                        <v-card elevation="0" class="pa-5">
                            <v-card-title>Promote Entire Class</v-card-title>
                            <v-card-subtitle>
                                Select a class to promote all students to the next grade level.
                                Grade 12 students will be moved to Alumni.
                            </v-card-subtitle>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-select
                                            v-model="selectedLevel"
                                            :items="formData.levels"
                                            item-text="name"
                                            item-value="id"
                                            label="Select Level"
                                            outlined
                                            dense
                                            @change="onLevelChange"
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-select
                                            v-model="selectedSubLevel"
                                            :items="subLevelsForLevel"
                                            item-text="name"
                                            item-value="id"
                                            label="Select Class/Grade"
                                            outlined
                                            dense
                                            :disabled="!selectedLevel"
                                            @change="onSubLevelChange"
                                        >
                                            <template v-slot:item="{ item }">
                                                {{ item.name }} ({{ getStudentCountForSubLevel(item.id) }} students)
                                            </template>
                                        </v-select>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="academicYear"
                                            label="Academic Year"
                                            outlined
                                            dense
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <!-- Preview Section -->
                                <div v-if="studentsInClass.length > 0" class="mt-5">
                                    <v-card outlined>
                                        <v-card-title>
                                            Students in {{ getSubLevelName(selectedSubLevel) }}
                                            <v-spacer></v-spacer>
                                            <v-chip color="primary">{{ studentsInClass.length }} students</v-chip>
                                        </v-card-title>
                                        <v-card-text>
                                            <v-simple-table>
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr class="accent">
                                                            <th>Name</th>
                                                            <th>Admission No</th>
                                                            <th>Current Class</th>
                                                            <th>Will Move To</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="student in studentsInClass" :key="student.id">
                                                            <td>{{ student.full_name }}</td>
                                                            <td>{{ student.admission_no }}</td>
                                                            <td>{{ student.current_level?.sub_level?.name || 'N/A' }}</td>
                                                            <td>
                                                                <span v-if="isGrade12(student)" class="success--text">
                                                                    <v-icon small color="success">mdi-school</v-icon>
                                                                    Graduate (Alumni)
                                                                </span>
                                                                <span v-else class="primary--text">
                                                                    <v-icon small color="primary">mdi-arrow-up</v-icon>
                                                                    {{ getNextGrade(student) }}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </v-card-text>
                                    </v-card>
                                </div>

                                <div class="mt-5" align="right">
                                    <v-btn
                                        color="primary"
                                        :disabled="!selectedSubLevel || studentsInClass.length === 0"
                                        :loading="previewLoading"
                                        @click="previewClassPromotion"
                                    >
                                        <v-icon left>mdi-eye</v-icon>
                                        Preview & Promote Class
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>

                    <!-- Manual Promotion Tab -->
                    <v-tab-item>
                        <v-card elevation="0" class="pa-5">
                            <v-card-title>Manual Student Promotion</v-card-title>
                            <v-card-subtitle>
                                Promote individual students to a specific grade.
                            </v-card-subtitle>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-select
                                            v-model="manualSelectedLevel"
                                            :items="formData.levels"
                                            item-text="name"
                                            item-value="id"
                                            label="Filter by Current Level"
                                            outlined
                                            dense
                                            clearable
                                            @change="onManualLevelChange"
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-select
                                            v-model="manualSelectedSubLevel"
                                            :items="manualSubLevelsForLevel"
                                            item-text="name"
                                            item-value="id"
                                            label="Filter by Current Class"
                                            outlined
                                            dense
                                            clearable
                                            :disabled="!manualSelectedLevel"
                                            @change="loadStudentsForManual"
                                        ></v-select>
                                    </v-col>
                                </v-row>

                                <div v-if="manualStudentsList.length > 0" class="mt-5">
                                    <v-card outlined>
                                        <v-card-title>
                                            Select Students to Promote
                                            <v-spacer></v-spacer>
                                            <v-chip color="info" v-if="selectedStudents.length > 0">
                                                {{ selectedStudents.length }} selected
                                            </v-chip>
                                        </v-card-title>
                                        <v-card-text>
                                            <v-data-table
                                                v-model="selectedStudents"
                                                :headers="studentHeaders"
                                                :items="manualStudentsList"
                                                show-select
                                                item-key="id"
                                                class="elevation-0"
                                            >
                                                <template v-slot:item.full_name="{ item }">
                                                    {{ item.full_name }}
                                                </template>
                                                <template v-slot:item.current_level="{ item }">
                                                    {{ item.current_level?.sub_level?.name || 'N/A' }}
                                                </template>
                                            </v-data-table>
                                        </v-card-text>
                                    </v-card>

                                    <!-- Target Grade Selection -->
                                    <v-card outlined class="mt-5" v-if="selectedStudents.length > 0">
                                        <v-card-title>Select Target Grade</v-card-title>
                                        <v-card-text>
                                            <v-row>
                                                <v-col cols="12" md="6">
                                                    <v-select
                                                        v-model="targetLevel"
                                                        :items="formData.levels"
                                                        item-text="name"
                                                        item-value="id"
                                                        label="Target Level"
                                                        outlined
                                                        dense
                                                        @change="onTargetLevelChange"
                                                    ></v-select>
                                                </v-col>
                                                <v-col cols="12" md="6">
                                                    <v-select
                                                        v-model="targetSubLevel"
                                                        :items="targetSubLevelsForLevel"
                                                        item-text="name"
                                                        item-value="id"
                                                        label="Target Class/Grade"
                                                        outlined
                                                        dense
                                                        :disabled="!targetLevel"
                                                    ></v-select>
                                                </v-col>
                                            </v-row>
                                            <v-textarea
                                                v-model="promotionNotes"
                                                label="Notes (optional)"
                                                outlined
                                                dense
                                                rows="2"
                                            ></v-textarea>
                                        </v-card-text>
                                    </v-card>

                                    <div class="mt-5" align="right">
                                        <v-btn
                                            color="primary"
                                            :disabled="selectedStudents.length === 0 || !targetSubLevel"
                                            :loading="promotionLoading"
                                            @click="promoteSelectedStudents"
                                        >
                                            <v-icon left>mdi-account-arrow-up</v-icon>
                                            Promote {{ selectedStudents.length }} Student(s)
                                        </v-btn>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>

                    <!-- Promotion History Tab -->
                    <v-tab-item>
                        <v-card elevation="0" class="pa-5">
                            <v-card-title>Promotion History</v-card-title>
                            <v-card-subtitle>
                                View all promotions and revert if needed. Reverting will move the student back to their previous class.
                            </v-card-subtitle>
                            <v-card-text>
                                <!-- Bulk Actions Bar -->
                                <v-alert
                                    v-if="selectedPromotions.length > 0"
                                    type="info"
                                    dense
                                    class="mb-4"
                                >
                                    <v-row align="center">
                                        <v-col>
                                            <strong>{{ selectedPromotions.length }}</strong> promotion(s) selected
                                        </v-col>
                                        <v-col class="text-right">
                                            <v-btn
                                                small
                                                color="warning"
                                                @click="openBulkRevertDialog"
                                            >
                                                <v-icon left small>mdi-undo</v-icon>
                                                Bulk Revert Selected
                                            </v-btn>
                                            <v-btn
                                                small
                                                text
                                                @click="selectedPromotions = []"
                                                class="ml-2"
                                            >
                                                Clear Selection
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-alert>

                                <v-progress-linear v-if="promotionLoader" height="2" indeterminate color="primary"></v-progress-linear>

                                <v-data-table
                                    v-if="promotions.length > 0"
                                    v-model="selectedPromotions"
                                    :headers="promotionHeaders"
                                    :items="promotions"
                                    show-select
                                    item-key="id"
                                    :loading="promotionLoader"
                                    class="elevation-1"
                                    hide-default-footer
                                >
                                    <template v-slot:item.student="{ item }">
                                        {{ item.student?.full_name }}
                                    </template>
                                    <template v-slot:item.from_sub_level="{ item }">
                                        {{ item.from_sub_level?.name || 'N/A' }}
                                    </template>
                                    <template v-slot:item.to_sub_level="{ item }">
                                        <span v-if="item.to_sub_level">
                                            {{ item.to_sub_level.name }}
                                        </span>
                                        <v-chip v-else x-small color="success">Graduate</v-chip>
                                    </template>
                                    <template v-slot:item.type="{ item }">
                                        <v-chip x-small :color="getPromotionTypeColor(item.type)">
                                            {{ item.type }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:item.promoted_by="{ item }">
                                        {{ item.promoted_by?.name || 'System' }}
                                    </template>
                                    <template v-slot:item.created_at="{ item }">
                                        {{ item.created_at | moment("DD/MM/YYYY HH:mm") }}
                                    </template>
                                    <template v-slot:item.actions="{ item }">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    icon
                                                    small
                                                    color="warning"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="openRevertDialog(item)"
                                                >
                                                    <v-icon small>mdi-undo</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Revert this promotion</span>
                                        </v-tooltip>
                                    </template>
                                </v-data-table>

                                <div v-else class="text-center pa-5">
                                    <v-icon large>mdi-history</v-icon>
                                    <p>No promotion history found</p>
                                </div>

                                <v-pagination
                                    v-if="promotionPagination.total > promotionPagination.per_page"
                                    :length="Math.ceil(promotionPagination.total / promotionPagination.per_page)"
                                    v-model="promotionPagination.current_page"
                                    @input="loadPromotionHistory"
                                    class="mt-5"
                                ></v-pagination>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>

                    <!-- Alumni Tab -->
                    <v-tab-item>
                        <v-card elevation="0" class="pa-5">
                            <v-card-title>
                                Alumni Students
                                <v-spacer></v-spacer>
                                <v-text-field
                                    v-model="alumniSearch"
                                    append-icon="mdi-magnify"
                                    label="Search alumni"
                                    outlined
                                    dense
                                    hide-details
                                    style="max-width: 300px"
                                    @keyup.enter="searchAlumni"
                                ></v-text-field>
                            </v-card-title>
                            <v-card-text>
                                <v-progress-linear v-if="promotionLoader" height="2" indeterminate color="primary"></v-progress-linear>
                                <v-simple-table v-if="alumni.length > 0">
                                    <template v-slot:default>
                                        <thead>
                                            <tr class="accent">
                                                <th>Name</th>
                                                <th>Admission No</th>
                                                <th>Account No</th>
                                                <th>Admission Year</th>
                                                <th>Graduation Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="student in alumni" :key="student.id">
                                                <td>{{ student.full_name }}</td>
                                                <td>{{ student.admission_no }}</td>
                                                <td>{{ student.account_no }}</td>
                                                <td>{{ student.admission_year }}</td>
                                                <td>{{ student.graduation_date | moment("DD/MM/YYYY") }}</td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                                <div v-else class="text-center pa-5">
                                    <v-icon large>mdi-school</v-icon>
                                    <p>No alumni found</p>
                                </div>

                                <v-pagination
                                    v-if="alumniPagination.total > alumniPagination.per_page"
                                    :length="Math.ceil(alumniPagination.total / alumniPagination.per_page)"
                                    v-model="alumniPagination.current_page"
                                    @input="loadAlumni"
                                    class="mt-5"
                                ></v-pagination>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-layout>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'PromotionAdmin',
    data() {
        return {
            mainTab: 0,
            snackbar: false,
            message: '',
            color: '',

            // Class Promotion
            selectedLevel: null,
            selectedSubLevel: null,
            academicYear: new Date().getFullYear().toString(),
            previewLoading: false,
            promotionLoading: false,
            confirmDialog: false,

            // Manual Promotion
            manualSelectedLevel: null,
            manualSelectedSubLevel: null,
            manualStudentsList: [],
            selectedStudents: [],
            targetLevel: null,
            targetSubLevel: null,
            promotionNotes: '',

            studentHeaders: [
                { text: 'Name', value: 'full_name' },
                { text: 'Admission No', value: 'admission_no' },
                { text: 'Current Class', value: 'current_level' },
            ],

            // Alumni
            alumniSearch: '',

            // Revert
            revertDialog: false,
            revertLoading: false,
            promotionToRevert: null,

            // Bulk Revert
            bulkRevertDialog: false,
            bulkRevertLoading: false,
            selectedPromotions: [],

            // Promotion History Headers
            promotionHeaders: [
                { text: 'Student', value: 'student' },
                { text: 'From', value: 'from_sub_level' },
                { text: 'To', value: 'to_sub_level' },
                { text: 'Type', value: 'type' },
                { text: 'Year', value: 'academic_year' },
                { text: 'Promoted By', value: 'promoted_by' },
                { text: 'Date', value: 'created_at' },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
        }
    },
    computed: {
        ...mapGetters([
            'promotionLoader',
            'promotions',
            'promotionPagination',
            'promotionFormData',
            'studentsInClass',
            'alumni',
            'alumniPagination',
            'promotionPreview',
        ]),
        formData() {
            return this.promotionFormData || { levels: [], sub_levels: [] }
        },
        subLevelsForLevel() {
            if (!this.selectedLevel || !this.formData.levels) return []
            const level = this.formData.levels.find(l => l.id === this.selectedLevel)
            return level ? level.sub_levels : []
        },
        manualSubLevelsForLevel() {
            if (!this.manualSelectedLevel || !this.formData.levels) return []
            const level = this.formData.levels.find(l => l.id === this.manualSelectedLevel)
            return level ? level.sub_levels : []
        },
        targetSubLevelsForLevel() {
            if (!this.targetLevel || !this.formData.levels) return []
            const level = this.formData.levels.find(l => l.id === this.targetLevel)
            return level ? level.sub_levels : []
        },
    },
    methods: {
        ...mapActions([
            'fetchPromotions',
            'fetchPromotionFormData',
            'fetchStudentsInClass',
            'fetchAlumni',
            'fetchPromotionPreview',
            'promoteSingleStudent',
            'promoteBulkStudents',
            'promoteEntireClass',
            'revertPromotion',
            'bulkRevertPromotions',
            'startPromotionLoader',
            'stopPromotionLoader',
            'clearStudentsInClass',
            'clearPromotionPreview',
        ]),
        async initialize() {
            await this.fetchPromotionFormData()
            await this.loadPromotionHistory(1)
        },
        onLevelChange() {
            this.selectedSubLevel = null
            this.clearStudentsInClass()
        },
        async onSubLevelChange() {
            if (this.selectedSubLevel) {
                await this.fetchStudentsInClass(this.selectedSubLevel)
            }
        },
        onManualLevelChange() {
            this.manualSelectedSubLevel = null
            this.manualStudentsList = []
            this.selectedStudents = []
        },
        async loadStudentsForManual() {
            if (this.manualSelectedSubLevel) {
                await this.fetchStudentsInClass(this.manualSelectedSubLevel)
                this.manualStudentsList = [...this.studentsInClass]
            }
        },
        onTargetLevelChange() {
            this.targetSubLevel = null
        },
        getStudentCountForSubLevel(subLevelId) {
            const subLevel = this.formData.sub_levels?.find(sl => sl.id === subLevelId)
            return subLevel?.student_count || 0
        },
        getSubLevelName(subLevelId) {
            const subLevel = this.formData.sub_levels?.find(sl => sl.id === subLevelId)
            return subLevel?.name || ''
        },
        isFinalYear(student) {
            const currentGrade = student.current_level?.sub_level?.name || ''
            // Check for Grade 12 or Form 4
            return currentGrade.includes('Grade 12') || currentGrade.includes('Form 4')
        },
        isGrade12(student) {
            // Alias for backwards compatibility
            return this.isFinalYear(student)
        },
        getNextGrade(student) {
            const currentGrade = student.current_level?.sub_level?.name || ''

            // Check for Form system
            const formMatch = currentGrade.match(/Form\s*(\d+)/i)
            if (formMatch) {
                const currentNum = parseInt(formMatch[1])
                if (currentNum >= 4) return 'Graduate (Alumni)'
                return `Form ${currentNum + 1}`
            }

            // Check for Grade system
            const gradeMatch = currentGrade.match(/Grade\s*(\d+)/i)
            if (gradeMatch) {
                const currentNum = parseInt(gradeMatch[1])
                if (currentNum >= 12) return 'Graduate (Alumni)'
                return `Grade ${currentNum + 1}`
            }

            return 'Next Grade'
        },
        async previewClassPromotion() {
            this.previewLoading = true
            try {
                await this.fetchPromotionPreview(this.selectedSubLevel)
                this.confirmDialog = true
            } catch (error) {
                this.showSnackbar('Failed to preview promotion', 'error')
            }
            this.previewLoading = false
        },
        async executePromotion() {
            this.promotionLoading = true
            try {
                const response = await this.promoteEntireClass({
                    from_sub_level_id: this.selectedSubLevel,
                    academic_year: this.academicYear
                })

                if (response.status === 'success') {
                    this.showSnackbar(response.message, 'success')
                    this.confirmDialog = false
                    this.selectedSubLevel = null
                    this.clearStudentsInClass()
                    this.clearPromotionPreview()
                    await this.loadPromotionHistory(1)
                } else {
                    this.showSnackbar(response.message || 'Promotion failed', 'error')
                }
            } catch (error) {
                this.showSnackbar('Failed to execute promotion', 'error')
            }
            this.promotionLoading = false
        },
        async promoteSelectedStudents() {
            this.promotionLoading = true
            try {
                const studentIds = this.selectedStudents.map(s => s.id)

                // For manual promotion with specific target, promote one by one
                let successCount = 0
                let errorCount = 0

                for (const studentId of studentIds) {
                    try {
                        const response = await this.promoteSingleStudent({
                            student_id: studentId,
                            to_sub_level_id: this.targetSubLevel,
                            academic_year: this.academicYear,
                            notes: this.promotionNotes
                        })

                        if (response.status === 'success') {
                            successCount++
                        } else {
                            errorCount++
                        }
                    } catch (e) {
                        errorCount++
                    }
                }

                this.showSnackbar(`Promoted ${successCount} students. ${errorCount} errors.`, successCount > 0 ? 'success' : 'error')

                this.selectedStudents = []
                this.targetSubLevel = null
                this.targetLevel = null
                this.promotionNotes = ''
                await this.loadStudentsForManual()
                await this.loadPromotionHistory(1)
            } catch (error) {
                this.showSnackbar('Failed to promote students', 'error')
            }
            this.promotionLoading = false
        },
        async loadPromotionHistory(page) {
            this.startPromotionLoader()
            await this.fetchPromotions(page)
        },
        async loadAlumni(page) {
            this.startPromotionLoader()
            await this.fetchAlumni({ page, search: this.alumniSearch })
        },
        async searchAlumni() {
            await this.loadAlumni(1)
        },
        getPromotionTypeColor(type) {
            switch (type) {
                case 'promotion': return 'primary'
                case 'graduation': return 'success'
                case 'manual': return 'info'
                default: return 'grey'
            }
        },
        openRevertDialog(promotion) {
            this.promotionToRevert = promotion
            this.revertDialog = true
        },
        async executeRevert() {
            if (!this.promotionToRevert) return

            this.revertLoading = true
            try {
                const response = await this.revertPromotion(this.promotionToRevert.id)

                if (response.status === 'success') {
                    this.showSnackbar(response.message, 'success')
                    this.revertDialog = false
                    this.promotionToRevert = null
                    await this.loadPromotionHistory(1)
                    await this.fetchPromotionFormData() // Refresh student counts
                } else {
                    this.showSnackbar(response.message || 'Revert failed', 'error')
                }
            } catch (error) {
                this.showSnackbar('Failed to revert promotion', 'error')
            }
            this.revertLoading = false
        },
        openBulkRevertDialog() {
            if (this.selectedPromotions.length === 0) {
                this.showSnackbar('Please select at least one promotion to revert', 'warning')
                return
            }
            this.bulkRevertDialog = true
        },
        async executeBulkRevert() {
            if (this.selectedPromotions.length === 0) return

            this.bulkRevertLoading = true
            try {
                const promotionIds = this.selectedPromotions.map(p => p.id)
                const response = await this.bulkRevertPromotions(promotionIds)

                if (response.status === 'success') {
                    this.showSnackbar(response.message, 'success')
                    this.bulkRevertDialog = false
                    this.selectedPromotions = []
                    await this.loadPromotionHistory(1)
                    await this.fetchPromotionFormData() // Refresh student counts
                } else {
                    this.showSnackbar(response.message || 'Bulk revert failed', 'error')
                }
            } catch (error) {
                this.showSnackbar('Failed to bulk revert promotions', 'error')
            }
            this.bulkRevertLoading = false
        },
        showSnackbar(message, color) {
            this.message = message
            this.color = color
            this.snackbar = true
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style scoped>
.v-data-table {
    border: 1px solid #e0e0e0;
}
</style>
